import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Check, CircleDashed, PackageCheck, ScanLine } from 'lucide-react';
import { loadLaboratoryScanners, receiveLaboratorySample, resolveLaboratoryCode, XomicsApiError } from './api';
import LaboratoryAcceptance from './LaboratoryAcceptance';
import { ContainerSymbol } from './XomicsCollections';
import { LabResource, TubeState, labError, labWhen } from './LaboratoryParts';
import { LabScanContext, useLabScan } from './labScanContext';
import { analysisName, ownSampleTypeName } from './LaboratoryText';
import { Head, XomicsSelect } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './laboratory-receipt.css';

const text = (key, values) => tx(`ui.lab.receipt.${key}`, values);
const SCANNER_KEY = 'xomics-lab-scanner';
const remembered = () => { try { return window.localStorage.getItem(SCANNER_KEY) || ''; } catch (error) { return ''; } };
const remember = value => { try { window.localStorage.setItem(SCANNER_KEY, value); } catch (error) { /* kept for this visit only */ } };

function Candidates({ candidates, onChoose }) {
  return <div className="xo-receipt-choice" role="group" aria-label={text('choose_tube')}>
    <p>{text('ambiguous')}</p>
    {candidates.map(candidate => <button type="button" key={candidate.sampleUuid} className="xo-receipt-candidate" onClick={() => onChoose(candidate.tubeCode)}>
      <code>{candidate.tubeCode}</code><span><strong>{candidate.organization.name}</strong><small>{candidate.theirCode} · {ownSampleTypeName(candidate.sampleType)}</small></span>
    </button>)}
  </div>;
}

function CollectionCard({ result, selected, onSelect }) {
  const collection = result.collection;
  return <article className="xo-receipt-collection">
    <header><div><span>{text('collection')}</span><h2><code>{collection.printedCode ?? collection.code}</code></h2><p>{ownSampleTypeName(collection.sampleType)}</p></div>
      {collection.organization && <div className="xo-receipt-organization"><span>{text('organization')}</span><strong>{collection.organization.name}</strong><small>{collection.organization.code}</small></div>}
    </header>
    <div className="xo-receipt-analyses">{collection.analyses.map(analysis => <span key={analysis.code} title={analysisName(analysis.code)}>{analysis.code}</span>)}
      {collection.analyses.length === 0 && <span className="is-empty">{text('no_analysis')}</span>}</div>
    <ul className="xo-receipt-tubes">{collection.tubes.map(tube => <li key={tube.uuid}>
      <button type="button" aria-pressed={selected === tube.uuid} className={selected === tube.uuid ? 'is-active' : ''} onClick={() => onSelect(tube)}>
        <span className="xo-receipt-tube-symbol"><ContainerSymbol code={tube.container.code} /></span>
        <span className="xo-receipt-tube-codes"><code>{tube.printedCode ?? tube.code}</code>{tube.organizationCode && <small>{text('their_code', { code: tube.organizationCode })}</small>}</span>
        <span className="xo-receipt-tube-received">{tube.receivedAt === null ? <><CircleDashed aria-hidden="true" />{text('not_received')}</>
          : <><Check aria-hidden="true" />{labWhen(tube.receivedAt)}</>}</span>
        <TubeState value={tube.state} />
      </button>
    </li>)}</ul>
  </article>;
}

export default function XomicsLabReceipt() {
  const scanners = useLiveResource(['laboratory'], loadLaboratoryScanners);
  // The scanner in use is the station's, shared by every laboratory page.
  const station = useContext(LabScanContext);
  const [ownScanner, setOwnScanner] = useState(remembered());
  const scanner = station ? station.scanner : ownScanner;
  const chooseScanner = (value) => { if (station) station.chooseScanner(value); else { setOwnScanner(value); remember(value); } };
  const [code, setCode] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [result, setResult] = useState(null), [candidates, setCandidates] = useState(null), [selected, setSelected] = useState(null);
  const [session, setSession] = useState([]);
  const input = useRef(null);
  useEffect(() => { if (input.current) input.current.focus(); }, [scanners.data]);
  const receive = useCallback(async (tube, resolved) => {
    const data = scanners.data;
    const received = await receiveLaboratorySample({ sampleCode: tube.code, accountId: data.accountId, deviceId: scanner });
    // Shown as printed on the kit (GH453369-01); `code` stays the tube's own code.
    setSession(current => [{ code: tube.code, label: tube.printedCode ?? tube.code,
      collection: resolved.collection.printedCode ?? resolved.collection.code, at: received.sample.receivedAt, replayed: received.replayed },
      ...current.filter(row => row.code !== tube.code)].slice(0, 20));
    return resolveLaboratoryCode(tube.code);
  }, [scanners.data, scanner]);
  async function open(value) {
    const trimmed = value.trim();
    if (trimmed === '' || busy) return null;
    setBusy(true); setError(''); setCandidates(null);
    try {
      let resolved = await resolveLaboratoryCode(trimmed);
      const tube = resolved.selectedTubeUuid === null ? null : resolved.collection.tubes.find(row => row.uuid === resolved.selectedTubeUuid);
      const arriving = Boolean(tube) && tube.state === 'awaiting_receipt';
      if (arriving) {
        if (scanner === '') throw Object.assign(new Error(text('choose_scanner_first')), { local: true });
        resolved = await receive(tube, resolved);
      }
      setResult(resolved); setSelected(resolved.selectedTubeUuid); setCode('');
      // What the scan did, for the station's own line.
      return { tone: 'ok', code: tube ? (tube.printedCode ?? tube.code) : (resolved.collection.printedCode ?? resolved.collection.code), detail: ownSampleTypeName(resolved.collection.sampleType),
        title: arriving ? tx('ui.lab.scan.received') : tube ? tx(`ui.lab.state.${tube.state}`) : text('choose_tube') };
    } catch (caught) {
      let message = caught.message;
      if (caught.local) setError(caught.message);
      else if (caught instanceof XomicsApiError && caught.code === 'SAMPLE_CODE_AMBIGUOUS' && caught.body && Array.isArray(caught.body.candidates)) {
        setCandidates(caught.body.candidates); setResult(null); message = text('ambiguous');
      } else { message = labError(caught); setError(message); }
      return { tone: 'bad', title: message };
    } finally { setBusy(false); if (input.current) input.current.focus(); }
  }
  // Every scan on this page is a tube arriving, wherever the cursor is.
  useLabScan({ match: code => () => (station ? station.guarded(code, () => open(code)) : open(code)) });
  async function choose(tube) {
    setSelected(tube.uuid); setError('');
    if (tube.state !== 'awaiting_receipt') return;
    if (scanner === '') { setError(text('choose_scanner_first')); return; }
    setBusy(true);
    try { setResult(await receive(tube, result)); } catch (caught) { setError(labError(caught)); }
    finally { setBusy(false); }
  }
  const tube = result && selected ? result.collection.tubes.find(row => row.uuid === selected) : null;
  return <div className="xo-products-page xo-receipt-page">
    <Head title={text('title')} subtitle={text('subtitle')} />
    <LabResource resource={scanners}>{data => <div className="xo-receipt-bar">
      <label className="xo-receipt-scanner"><span>{text('scanner')}</span>
        <XomicsSelect value={scanner} onChange={event => chooseScanner(event.target.value)} aria-label={text('scanner')}>
          <option value="">{text('choose_scanner')}</option>
          {data.scanners.map(item => <option key={item.deviceId} value={item.deviceId}>{item.deviceId} · {item.manufacturer} {item.model}</option>)}
        </XomicsSelect></label>
      <form className="xo-receipt-scan" onSubmit={event => { event.preventDefault(); open(code); }}>
        <ScanLine aria-hidden="true" />
        <input ref={input} value={code} onChange={event => setCode(event.target.value)} maxLength={64} placeholder={text('scan_placeholder')} data-scan-route=""
          aria-label={text('scan')} disabled={busy} autoComplete="off" spellCheck="false" />
        <button type="submit" className="xo-btn" disabled={busy || code.trim() === ''}>{text(busy ? 'working' : 'open')}</button>
      </form>
    </div>}</LabResource>
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    {candidates && <Candidates candidates={candidates} onChoose={value => open(value)} />}
    <div className="xo-receipt-layout">
      <div className="xo-receipt-main">
        {result ? <CollectionCard result={result} selected={selected} onSelect={choose} />
          : <div className="xo-receipt-empty"><PackageCheck aria-hidden="true" /><strong>{text('empty')}</strong><p>{text('empty_hint')}</p></div>}
        {session.length > 0 && <section className="xo-receipt-session" aria-label={text('session')}>
          <h3>{text('session')}</h3>
          <ol>{session.map(row => <li key={row.code}><code>{row.label ?? row.code}</code>{row.collection !== (row.label ?? row.code) && <span>{row.collection}</span>}
            <small>{row.replayed ? text('already_received') : labWhen(row.at)}</small></li>)}</ol>
        </section>}
      </div>
      <aside className="xo-receipt-side">
        {tube && tube.receivedHere ? <LaboratoryAcceptance key={tube.uuid} sampleUuid={tube.uuid} onRecorded={() => resolveLaboratoryCode(tube.code).then(setResult).catch(() => {})} />
          : <div className="xo-receipt-hint"><p>{text(tube ? 'receive_first' : 'choose_hint')}</p></div>}
      </aside>
    </div>
  </div>;
}
